/**
 * SplashScreen — écran d'authentification UNIQUE SaaS.
 *
 * Phases :
 *   1. "splash"      → logo animé (1.6s)
 *   2. "credentials" → login email + password  |  bascule "register"
 *   3. "register"    → création de compte
 *   4. "connecting"  → spinner dorés, puis onSuccess()
 *
 * Appelle AuthService.login() / AuthService.register() vers le backend FastAPI.
 */
import { useEffect, useState } from 'react';
import type { FormEvent, JSX, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Sparkles, UserPlus } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { useUserStore } from '@store/userStore';
import { AuthError } from '@services/AuthService';

type Phase = 'splash' | 'credentials' | 'register' | 'connecting';

export interface SplashScreenProps {
  onSuccess?: (email: string) => void;
}

export default function SplashScreen({ onSuccess }: SplashScreenProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('splash');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const login    = useUserStore((s) => s.login);
  const register = useUserStore((s) => s.register);

  // Passage auto splash → credentials après 1.6s
  useEffect(() => {
    if (phase !== 'splash') return;
    const t = window.setTimeout(() => setPhase('credentials'), 1600);
    return () => window.clearTimeout(t);
  }, [phase]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      setPhase('connecting');
      window.setTimeout(() => onSuccess?.(email), 1400);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Erreur de connexion.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Le mot de passe doit comporter au moins 8 caractères.');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, firstName, lastName);
      setPhase('connecting');
      window.setTimeout(() => onSuccess?.(email), 1400);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Erreur lors de la création du compte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="unique-surface flex items-center justify-center p-6">
      {/* Halo décoratif en fond */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-60"
          style={{ background: 'var(--color-primary-glow)' }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-50"
          style={{ background: 'var(--color-primary-light)' }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="unique-card relative z-10 w-full max-w-md p-10"
      >
        {/* Logo + pulsation dorée (toujours affiché en haut) */}
        <div className="flex flex-col items-center text-center mb-8">
          <motion.div
            className="unique-pulse w-20 h-20 rounded-full flex items-center justify-center mb-5"
            style={{
              background:
                'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
            }}
            animate={{ rotate: phase === 'connecting' ? 360 : 0 }}
            transition={{
              rotate: {
                repeat: phase === 'connecting' ? Infinity : 0,
                duration: 1.4,
                ease: 'linear',
              },
            }}
          >
            <Sparkles size={36} color="#1A1A1A" strokeWidth={2.4} />
          </motion.div>
          <h1 className="!border-none !text-3xl !pb-0 tracking-tight">UNIQUE</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-medium)]">
            Votre espace de travail intelligent
          </p>
        </div>

        <AnimatePresence mode="wait">
          {phase === 'splash' && (
            <motion.div
              key="splash"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center"
            >
              <div className="unique-loader" aria-label="Chargement" />
            </motion.div>
          )}

          {phase === 'credentials' && (
            <motion.form
              key="credentials"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              onSubmit={handleLogin}
              className="flex flex-col gap-4"
              noValidate
            >
              <LabeledInput id="email" label="Adresse email" type="email"
                icon={<Mail size={18} />} value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@entreprise.bf" autoComplete="email" required
              />
              <LabeledInput id="password" label="Mot de passe" type="password"
                icon={<Lock size={18} />} value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" required
              />

              {error && <ErrorText message={error} />}

              <UniqueButton type="submit" variant="primary" size="lg" fullWidth
                isLoading={loading} rightIcon={<ArrowRight size={18} />} className="mt-2"
              >
                Se connecter
              </UniqueButton>

              <button type="button"
                onClick={() => { setError(null); setPhase('register'); }}
                className="text-xs text-center transition-colors"
                style={{ color: 'var(--color-text-medium)' }}
              >
                Pas encore de compte ?{' '}
                <span style={{ color: 'var(--color-primary-dark)', fontWeight: 700 }}>
                  Créer un compte →
                </span>
              </button>
            </motion.form>
          )}

          {phase === 'register' && (
            <motion.form
              key="register"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              onSubmit={handleRegister}
              className="flex flex-col gap-3"
              noValidate
            >
              <div className="grid grid-cols-2 gap-3">
                <LabeledInput id="firstName" label="Prénom" type="text"
                  icon={<User size={18} />} value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Prénom" autoComplete="given-name" required
                />
                <LabeledInput id="lastName" label="Nom" type="text"
                  icon={<User size={18} />} value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Nom" autoComplete="family-name" required
                />
              </div>
              <LabeledInput id="reg-email" label="Adresse email" type="email"
                icon={<Mail size={18} />} value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@entreprise.bf" autoComplete="email" required
              />
              <LabeledInput id="reg-password" label="Mot de passe (8 car. min.)" type="password"
                icon={<Lock size={18} />} value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="new-password" required
              />

              {error && <ErrorText message={error} />}

              <UniqueButton type="submit" variant="primary" size="lg" fullWidth
                isLoading={loading} leftIcon={<UserPlus size={18} />} className="mt-1"
              >
                Créer mon compte
              </UniqueButton>

              <button type="button"
                onClick={() => { setError(null); setPhase('credentials'); }}
                className="text-xs text-center transition-colors"
                style={{ color: 'var(--color-text-medium)' }}
              >
                ← Retour à la connexion
              </button>
            </motion.form>
          )}

          {phase === 'connecting' && (
            <motion.div
              key="connecting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-4"
            >
              <div className="relative w-24 h-24 mb-4">
                {[0, 0.3, 0.6].map((delay, i) => (
                  <motion.span
                    key={i}
                    className="absolute inset-0 rounded-full border-2"
                    style={{ borderColor: 'var(--color-primary)' }}
                    initial={{ scale: 0.4, opacity: 0.9 }}
                    animate={{ scale: 1.4, opacity: 0 }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      delay,
                      ease: 'easeOut',
                    }}
                  />
                ))}
                <div
                  className="absolute inset-0 m-auto w-10 h-10 rounded-full"
                  style={{ background: 'var(--color-primary)' }}
                />
              </div>
              <p className="text-sm text-[color:var(--color-text-medium)]">
                Ouverture de votre cockpit…
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-8 text-center text-[11px] text-[color:var(--color-text-medium)]">
          © {new Date().getFullYear()} UNIQUE — Burkina Faso
        </p>
      </motion.div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sous-composants locaux                                                     */
/* -------------------------------------------------------------------------- */

interface LabeledInputProps {
  id: string;
  label: string;
  type: string;
  icon: JSX.Element;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}

function LabeledInput({
  id,
  label,
  type,
  icon,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
}: LabeledInputProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-xs font-semibold text-[color:var(--color-text-dark)] tracking-wide uppercase"
      >
        {label}
      </label>
      <div className="relative">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-primary-dark)]"
          aria-hidden
        >
          {icon}
        </span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          className="unique-input !pl-10"
        />
      </div>
    </div>
  );
}

function ErrorText({ message }: { message: string }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      role="alert"
      className="text-xs font-medium text-[color:var(--color-urgent)] bg-[rgba(239,83,80,0.08)] border border-[rgba(239,83,80,0.3)] rounded-lg px-3 py-2"
    >
      {message}
    </motion.p>
  );
}
